import React from 'react';
import { formatUserName } from '../utils/format/formatUserName';
import { cn } from '../ui/cn';


// Універсальний інтерфейс для користувача
export interface UserAvatarData {
  photo_url?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  full_name?: string | null; // Повне ім'я (якщо first_name/last_name не доступні)
  username?: string | null;
  vip_until?: string | null; // ISO datetime string або null
  is_vip?: boolean; // Pre-computed VIP flag (e.g. from CursorUserInfo)
}

interface UserAvatarProps {
  user: UserAvatarData | null | undefined;
  size?: number; // Розмір в пікселях (за замовчуванням 58)
  onClick?: (e: React.MouseEvent) => void;
  backgroundColor?: string; // Колір фону для fallback (за замовчуванням var(--color-white-alpha-20))
  textColor?: string; // Колір тексту для fallback (за замовчуванням var(--color-white))
  borderColor?: string; // Колір бордера (наприклад "var(--color-brand)")
  borderWidth?: number; // Ширина бордера в пікселях (за замовчуванням 2)
  alt?: string;
  style?: React.CSSProperties;
}

const VIP_BORDER_IMAGE = '/ui/avatar/vip-status-avatar-border.png';
const VIP_BADGE_IMAGE = '/ui/avatar/vip-status-avatar-border.png';

// Функція для перевірки, чи користувач має активний VIP статус
const isVipActive = (vipUntil: string | null | undefined): boolean => {
  if (!vipUntil) return false;
  try {
    const vipEndDate = new Date(vipUntil);
    const now = new Date();
    return vipEndDate > now;
  } catch {
    return false;
  }
};

export default function UserAvatar({
  user,
  size = 58,
  onClick,
  textColor,
  borderColor,
  borderWidth = 2,
  alt = '',
  style,
}: UserAvatarProps) {
  if (!user) {
    return null;
  }

  const isVip = user.is_vip === true || isVipActive(user.vip_until);
  const photoUrl = user.photo_url;

  const { displayName, fallbackLetter } = formatUserName(user);

  // Розмір VIP значка (24px згідно з Figma)
  const vipBadgeSize = 24;
  // Позиція VIP значка: центром на бордері (внизу аватарки)
  // Якщо size = 58, то badge має бути на позиції bottom: -12px (половина розміру badge)
  const vipBadgeBottom = -(vipBadgeSize / 2);

  const avatarContent = photoUrl ? (
    <img
      src={photoUrl}
      alt={alt || displayName}
      loading="lazy"
      decoding="async"
      className="block h-full w-full rounded-full object-cover"
    />
  ) : (
    <div
      className={cn(
        "flex h-full w-full items-center justify-center rounded-full bg-black [font-family:Lato,sans-serif] font-bold",
        !textColor && "text-white",
      )}
      style={{
        ...(textColor ? { color: textColor } : {}), // dynamic prop
        fontSize: `${Math.floor(size * 0.414)}px`, // size prop
      }}
    >
      {fallbackLetter.toUpperCase()}
    </div>
  );

  const containerClassName = cn(
    "relative block shrink-0",
    onClick ? "cursor-pointer" : "cursor-default",
    borderColor && "box-border overflow-hidden rounded-full",
  );

  const containerStyle: React.CSSProperties = {
    width: `${size}px`, // size prop
    height: `${size}px`, // size prop
    ...(borderColor
      ? { border: `${borderWidth}px solid ${borderColor}` } // dynamic props
      : {}),
    ...style, // dynamic prop override
  };

  if (isVip) {
    return (
      <div onClick={onClick} className={containerClassName} style={containerStyle}>
        {/* VIP рамка та аватарка */}
        <div
          className="box-border h-full w-full overflow-hidden rounded-full border-[3px] border-solid border-accent"
          style={{
            // dynamic: VIP border image only when photo present
            background: photoUrl
              ? `url(${VIP_BORDER_IMAGE}) lightgray 50% / cover no-repeat`
              : 'transparent',
          }}
        >
          {avatarContent}
        </div>
        {/* VIP значок внизу (тільки якщо є фото) */}
        {photoUrl && (
          <img
            src={VIP_BADGE_IMAGE}
            alt="VIP"
            className="pointer-events-none absolute left-1/2 z-[1] h-6 w-6 -translate-x-1/2"
            style={{
              bottom: `${vipBadgeBottom}px`, // derived from constant badge size
            }}
          />
        )}
      </div>
    );
  }

  // Звичайна аватарка без VIP
  return (
    <div onClick={onClick} className={containerClassName} style={containerStyle}>
      {avatarContent}
    </div>
  );
}
